/** @jsx jsx */
import { jsx } from '@emotion/core';

import React, { useState, useEffect, useCallback } from 'react';
import Popover from './Popover';

interface PropTypes {
  id: string;
  textFieldRef: React.MutableRefObject<HTMLElement | null>;
}

/**
 * Popover component that anchors on a TextField.
 *
 * The popover opens on focus and closes on blur.
 * It also matches its width with the TextField.
 *
 * Usage:
 * ```
 * const ref = useRef<HTMLElement | null>(null)
 *
 * return (
 *   ...
 *   <TextField inputRef={ref} ... />
 *   <TextFieldPopover popperId="id" textFieldRef={ref}
 *     content
 *   </TextFieldPopover>
 *   ...
 * )
 *
 * ```
 */
const TextFieldPopover: React.FC<PropTypes> = props => {
  const { id: popperId, children, textFieldRef } = props;
  const [open, setOpen] = useState<boolean>(false);
  const ariaDescribedByLabel = 'aria-describedby'; // Avoid spelling mistakes

  const openPopper = useCallback(() => {
    setOpen(true);
  }, [setOpen]);
  const closePopper = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  // Close popover without losing focus (for accessibility reasons)
  const escFunction = useCallback(
    event => {
      if (event.code === 'Escape') {
        setOpen(false);
      }
    },
    [setOpen],
  );

  const addPopoverAriaDescribedby = useCallback(() => {
    if (!textFieldRef.current) {
      return;
    }
    const describedby = textFieldRef.current.getAttribute(ariaDescribedByLabel);
    const newDescribedby = describedby
      ? `${describedby} ${popperId}`
      : popperId; // Set to either the original + popperId, or just popperId
    textFieldRef.current.setAttribute(ariaDescribedByLabel, newDescribedby); // set the aria attribute
  }, [textFieldRef, popperId]);

  const removePopoverAriaDescribedby = useCallback(() => {
    if (!textFieldRef.current) {
      return;
    }
    const describedby = textFieldRef.current.getAttribute(ariaDescribedByLabel);
    if (!describedby) {
      return;
    }
    if (describedby === popperId) {
      // Remove attribute if there is no other describedby
      textFieldRef.current.removeAttribute(ariaDescribedByLabel);
    } else {
      // Revert back to original describedby
      const newDescribedby = describedby.replace(
        new RegExp(` ?${popperId}`),
        '',
      );
      textFieldRef.current.setAttribute(ariaDescribedByLabel, newDescribedby); // set the aria attribute
    }
  }, [textFieldRef, popperId]);

  useEffect(() => {
    let ref: HTMLElement | null = null;
    if (textFieldRef && textFieldRef.current) {
      ref = textFieldRef.current;
      ref.addEventListener('focus', openPopper);
      ref.addEventListener('blur', closePopper);
      ref.addEventListener('keydown', escFunction);
    }
    return () => {
      if (ref) {
        ref.removeEventListener('focus', openPopper);
        ref.removeEventListener('blur', openPopper);
        ref.removeEventListener('keydown', escFunction);
      }
    };
  }, [closePopper, openPopper, textFieldRef, escFunction]);

  useEffect(() => {
    if (open) {
      addPopoverAriaDescribedby();
    } else {
      removePopoverAriaDescribedby();
    }
  }, [open, addPopoverAriaDescribedby, removePopoverAriaDescribedby]);

  if (textFieldRef.current === null) {
    return null;
  }

  return (
    <Popover
      open={open}
      popperId={popperId}
      children={children}
      width={textFieldRef.current ? textFieldRef.current.offsetWidth + 4 : 0}
    />
  );
};

export default TextFieldPopover;
